// Achat à vie d'une formation : les montants et les dates, partagés par
// l'affichage (CourseAccessOptions) et le paiement (lib/courseLifetime.js).
// Aucun secret ici : le fichier est aussi chargé dans le navigateur.

import moment from "moment";

// Les seuls prix possibles, choisis dans /admin/course/[slug] : 149 € jusqu'à
// 10 heures de formation, 199 € au-delà (grille du 18/09/2026). Le back
// refuse tout autre montant (CourseLifetimeController::PRESET_PRICES).
export const LIFETIME_PRESET_PRICES = [149, 199];
export const LIFETIME_INSTALLMENTS = 3;

/** Le prix de l'achat à vie d'une formation, ou null s'il n'est pas ouvert. */
export function lifetimePriceOf(course) {
    const price = Number(course?.lifetimePrice);
    return course?.lifetimeEnabled && LIFETIME_PRESET_PRICES.includes(price)
        ? price
        : null;
}

/**
 * La promotion applicable à l'achat à vie d'une formation, ou null.
 *
 * DEUX SOURCES, une seule gagne : la promotion propre à la formation
 * (/admin/course/[slug]) l'emporte sur la promotion générale du site, qui ne
 * s'applique aux achats à vie que si l'interrupteur de /admin/promotions est
 * ouvert. Une remise dont la date est passée n'existe pas : on la relit ici,
 * parce que l'heure du navigateur peut avoir tourné depuis le rendu.
 *
 * DEUX UNITÉS, volontairement : la promotion d'une formation se définit par un
 * PRIX (l'équipe raisonne en « 89 € », et un pourcentage entier ne permet pas
 * d'atteindre tous les prix ronds), celle du site par un POURCENTAGE, qui
 * s'applique à tout le catalogue. Les deux ressortent d'ici sous la même
 * forme : un prix et le pourcentage correspondant.
 *
 * @param {object} offer    { promoPrice, promoEndsAt, promoLabelFr/En }
 * @param {object} commerce réglages commerciaux ({ promotion, lifetimePromo })
 * @param {number} basePrice prix plein de la formation
 * @returns {{price: number, percent: number, endsAt: string, source: "course"|"site"}|null}
 */
export function lifetimePromotionOf(offer, commerce, basePrice, locale = "fr") {
    const now = Date.now();
    const alive = (endsAt) => endsAt && new Date(endsAt).getTime() > now;
    const percentOf = (price) =>
        basePrice ? Math.round((1 - price / basePrice) * 100) : 0;

    const coursePrice = Number(offer?.promoPrice);
    if (coursePrice > 0 && coursePrice < basePrice && alive(offer.promoEndsAt)) {
        return {
            price: coursePrice,
            percent: percentOf(coursePrice),
            endsAt: offer.promoEndsAt,
            // Le nom de l'offre, s'il a été donné. Le français sert de repli :
            // mieux vaut « Offre de lancement » à un anglophone que rien.
            label:
                (locale === "en"
                    ? offer.promoLabelEn || offer.promoLabelFr
                    : offer.promoLabelFr || offer.promoLabelEn) || null,
            source: "course",
        };
    }

    const site = commerce?.promotion;
    if (
        commerce?.lifetimePromo?.enabled &&
        site?.discountPercent > 0 &&
        alive(site.endsAt)
    ) {
        const price = Math.max(
            1,
            Math.round(basePrice * (1 - site.discountPercent / 100)),
        );
        return {
            price,
            percent: percentOf(price),
            endsAt: site.endsAt,
            // La promotion du site porte déjà son nom.
            label: (locale === "en" ? site.nameEn : site.nameFr) || null,
            source: "site",
        };
    }

    return null;
}

/** Le pourcentage de remise qu'un prix cible représente, pour l'affichage. */
export function promotionPercent(basePrice, promoPrice) {
    if (!basePrice || !promoPrice || promoPrice >= basePrice) return 0;
    return Math.round((1 - promoPrice / basePrice) * 100);
}

/**
 * Les trois prélèvements du paiement en 3 fois, en centimes. Les deux
 * premiers sont arrondis au centime supérieur et le dernier prend le reste :
 * 149 € = 49,67 + 49,67 + 49,66. Aucune échéance ne dépasse le montant
 * annoncé (« 3 × 49,67 € ») et le total fait le prix exact.
 */
export function installmentAmounts(priceEuros) {
    const total = Math.round(priceEuros * 100);
    const regular = Math.ceil(total / LIFETIME_INSTALLMENTS);
    return [
        regular,
        regular,
        total - regular * (LIFETIME_INSTALLMENTS - 1),
    ];
}

/**
 * Dates des deuxième et troisième prélèvements : un et deux mois après le
 * premier. moment ramène le 31 janvier au 28 février, là où Date sauterait
 * en mars.
 */
export function installmentDates(firstPaymentDate = new Date()) {
    return [1, 2].map((months) =>
        moment(firstPaymentDate).add(months, "months").toDate(),
    );
}

/** « 49,67 » ou « 149 » : centimes affichés seulement s'il y en a. */
export function formatEuros(cents, locale) {
    const whole = cents % 100 === 0;
    return new Intl.NumberFormat(locale === "en" ? "en-GB" : "fr-FR", {
        minimumFractionDigits: whole ? 0 : 2,
        maximumFractionDigits: 2,
    }).format(cents / 100);
}

/** « 19 octobre » / « 19 October », à l'heure de Paris. */
export function formatDayMonth(date, locale) {
    return new Intl.DateTimeFormat(locale === "en" ? "en-GB" : "fr-FR", {
        day: "numeric",
        month: "long",
        timeZone: "Europe/Paris",
    }).format(date);
}
